import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useParcelContext } from '../../../../components/ParcelProvider';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Button from '@material-ui/core/Button';
import MuiLink from '@material-ui/core/Link';
import ParcelForm from '../../../../components/ParcelForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Property from '@afosto/app-components-react/Property';
import getShippingMethodName from '../../../../utils/getShippingMethodName';
import formatDateTime from '@afosto/utils/date/formatDateTime';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import { boolean as booleanFormatter } from '@afosto/app-components-react/DataGrid/formatters';
import { sizeFormatter } from '../../../ViewShipment/ShipmentParcels/formatters';
import translations from './translations';

const ParcelInformationSection = () => {
  const intl = useIntl();
  const { parcel, fetchParcel, shippingMethod, shipment } = useParcelContext();
  const { enqueueSnackbar } = useSnackbar();
  const [showEditParcel, setShowEditParcel] = useState(false);

  /**
   * Handle updating the parcel.
   * @param {date} shipAt
   * @param {string} trackingCode
   * @param {string} trackingUrl
   * @param {number} height
   * @param {number} width
   * @param {number} length
   * @param {number} weight
   * @param {string} position
   */
  const handleUpdateParcel = async ({
    shipAt,
    trackingCode,
    trackingUrl,
    height,
    width,
    length,
    weight,
    position,
  }) => {
    try {
      await parcelsApi.updateParcel(parcel.id, {
        ship_at: shipAt ?? null,
        tracking_code: trackingCode,
        tracking_url: trackingUrl,
        height: height ? parseInt(height, 10) : 0,
        width: width ? parseInt(width, 10) : 0,
        length: length ? parseInt(length, 10) : 0,
        weight: weight ? parseInt(weight, 10) : 0,
        position: position ? position?.toUpperCase() : null,
      });
      await fetchParcel();

      setShowEditParcel(false);

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotUpdated), {
        variant: 'error',
      });
    }
  };

  const getStatus = () => {
    if (parcel.is_finished) {
      return intl.formatMessage(translations.parcelInformationSection.overview.statuses.isFinished);
    }

    if (parcel.is_in_transit) {
      return intl.formatMessage(
        translations.parcelInformationSection.overview.statuses.isInTransit,
      );
    }

    return null;
  };

  return (
    <>
      <Card style={{ height: '100%' }}>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            parcel &&
            !parcel.is_in_transit &&
            !parcel.is_finished && (
              <Button color="primary" variant="outlined" onClick={() => setShowEditParcel(true)}>
                {intl.formatMessage(translations.parcelInformationSection.button.edit.label)}
              </Button>
            )
          }
        />
        {parcel && (
          <Box px={2} pb={2}>
            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.shipment)}
              value={
                <MuiLink variant="subtitle1" component={Link} to={`/shipments/${shipment?.id}`}>
                  {shipment?.number ?? 'Concept'}
                </MuiLink>
              }
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.isOutbound)}
              value={booleanFormatter(parcel?.is_outbound)}
            />

            <Property
              label={intl.formatMessage(
                translations.parcelInformationSection.overview.shippingMethod,
              )}
              value={getShippingMethodName(intl, shippingMethod)}
            />

            <Property
              label={intl.formatMessage(
                translations.parcelInformationSection.overview.trackingCode,
              )}
              value={parcel?.tracking_code}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.trackingUrl)}
              value={
                parcel?.tracking_url && (
                  <MuiLink variant="body2" href={parcel.tracking_url} target="_blank">
                    {parcel.tracking_url}
                  </MuiLink>
                )
              }
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.status)}
              value={getStatus()}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.createdAt)}
              value={parcel?.created_at && formatDateTime(parcel.created_at)}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.shipAt)}
              value={parcel?.ship_at && formatDateTime(parcel.ship_at)}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.inTransitAt)}
              value={parcel?.in_transit_at && formatDateTime(parcel.in_transit_at)}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.finishedAt)}
              value={parcel?.finished_at && formatDateTime(parcel.finished_at)}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.position)}
              value={parcel?.position}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.size)}
              value={sizeFormatter(null, parcel)}
            />

            <Property
              label={intl.formatMessage(translations.parcelInformationSection.overview.weight)}
              value={parcel?.weight}
            />
          </Box>
        )}
      </Card>
      <FormDrawer
        isOpen={showEditParcel}
        title={intl.formatMessage(translations.parcelInformationSection.editParcel.title)}
        drawerWidth="400px"
        onClose={() => setShowEditParcel(false)}
      >
        <ParcelForm
          onCancel={() => setShowEditParcel(false)}
          onSubmit={handleUpdateParcel}
          parcel={parcel}
        />
      </FormDrawer>
    </>
  );
};

export default ParcelInformationSection;
